import React from 'react';

import {
  AGE_OPTIONS,
  DEEPFAKE_STATE_OPTIONS_STRICT,
  GENDER_OPTIONS_STRICT,
} from '@vlabs/api-bindings/src/constants';
import { LIVENESS_OPTIONS_STRICT } from '@vlabs/api-bindings/src/luna-client/constants';

import { ViewRow, ViewRowSection } from '../components';
import { useHandlerViewPageTranslation } from '../i18n';
import { extractFilters, formatRange, hasFilters } from '../viewModel';
import { optionLabel } from './options';
import type { Option } from './options';

interface FilterRowsProps {
  filters: unknown;
}

function FilterRows({ filters }: FilterRowsProps): React.JSX.Element | null {
  const { t: tView } = useHandlerViewPageTranslation();
  const view = extractFilters(filters);
  if (!hasFilters(view)) return null;

  const genderLabel = view.gender !== undefined
    ? optionLabel(GENDER_OPTIONS_STRICT, view.gender)
    : undefined;

  const ageLabel = (view.ageGte !== undefined || view.ageLt !== undefined)
    // AGE_OPTIONS — optionsProxy со lookup по "gte_lte"
    ? (AGE_OPTIONS as unknown as Record<string, Option>)[`${view.ageGte ?? ''}_${view.ageLt ?? ''}`]?.label
    : undefined;

  const livenessLabel = (view.liveness ?? [])
    .map((state) => optionLabel(LIVENESS_OPTIONS_STRICT, state))
    .filter((label): label is string => !!label)
    .join(', ');

  const deepfakeLabel = (view.deepfake ?? [])
    .map((state) => optionLabel(DEEPFAKE_STATE_OPTIONS_STRICT, state))
    .filter((label): label is string => !!label)
    .join(', ');

  const matchItems = view.match ?? [];
  const similarityRows = matchItems
    .map((match, index) => {
      const similarity = formatRange(match.similarityGte, match.similarityLte);

      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`similarity-${match.label ?? ''}-${index}`}>
          {match.label
            ? (
              <ViewRow label={tView('filters.match_params')}>
                {match.label}
              </ViewRow>
            )
            : null}
          {similarity
            ? (
              <ViewRow label={tView('filters.similarity')}>
                {similarity}
              </ViewRow>
            )
            : null}
        </React.Fragment>
      );
    });

  const hasConditions = !!genderLabel || !!ageLabel || !!livenessLabel || !!deepfakeLabel;

  return (
    <React.Fragment>
      {!!hasConditions && (
        <React.Fragment>
          <ViewRowSection label={tView('filters.all_conditions')} />
          {genderLabel
            ? <ViewRow label={tView('filters.gender')}>{genderLabel}</ViewRow>
            : null}
          {ageLabel
            ? <ViewRow label={tView('filters.age')}>{ageLabel}</ViewRow>
            : null}
          {livenessLabel
            ? <ViewRow label={tView('filters.liveness')}>{livenessLabel}</ViewRow>
            : null}
          {deepfakeLabel
            ? <ViewRow label={tView('filters.deepfake')}>{deepfakeLabel}</ViewRow>
            : null}
        </React.Fragment>
      )}
      {similarityRows.length > 0 && (
        <React.Fragment>
          <ViewRowSection label={tView('filters.additional_conditions')} />
          {similarityRows}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export { FilterRows };
